import { User, AuthState } from '../types';

class AuthService {
  private static instance: AuthService;
  private authState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null
  };
  private listeners: ((state: AuthState) => void)[] = [];
  private isDemo: boolean;
  private isLocalMode: boolean;
  private cognitoConfig: {
    userPoolId: string;
    clientId: string;
    domain: string;
  } | null = null;

  private constructor() {
    // Check if in demo mode
    this.isDemo = process.env.REACT_APP_DEMO_MODE === 'true';
    
    // Check if in local development mode (localhost only!)
    this.isLocalMode = this.isValidLocalMode();
    
    // Initialize Cognito config if not in demo or local mode
    if (!this.isDemo && !this.isLocalMode) {
      this.cognitoConfig = {
        userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
        clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
        domain: process.env.REACT_APP_COGNITO_DOMAIN || ''
      };
    }
    
    // Initialize from localStorage if available
    this.loadFromStorage();
    
    // Auto-authenticate in local mode
    if (this.isLocalMode) {
      this.autoAuthenticateLocalMode();
    }
  }

  // Validate local mode is only enabled on localhost
  private isValidLocalMode(): boolean {
    const localModeEnabled = process.env.REACT_APP_LOCAL_MODE === 'true';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    if (localModeEnabled && !isLocalhost) {
      console.error('❌ LOCAL MODE ERROR: REACT_APP_LOCAL_MODE is enabled but not running on localhost!');
      console.error('   This is a SECURITY RISK and must not be deployed to production.');
      console.error('   Local mode only works on localhost/127.0.0.1');
      return false;
    }
    
    if (localModeEnabled && !isDevelopment) {
      console.error('❌ LOCAL MODE ERROR: REACT_APP_LOCAL_MODE is enabled in production build!');
      console.error('   This is a SECURITY RISK. Local mode only works in development.');
      return false;
    }
    
    if (localModeEnabled) {
      console.log('🏠 Local development mode enabled for dan@zone4.ca');
    }
    
    return localModeEnabled && isLocalhost && isDevelopment;
  }

  // Auto-authenticate with dan@zone4.ca in local mode
  private async autoAuthenticateLocalMode(): Promise<void> {
    try {
      const localUser: User = {
        id: await this.generateUserId('dan@zone4.ca'),
        email: 'dan@zone4.ca',
        name: 'Dan (Local Dev)',
        provider: 'local',
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      };

      this.setAuthState({
        user: localUser,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
      console.log('🔐 Auto-authenticated as dan@zone4.ca (local mode)');
    } catch (error) {
      console.error('Failed to auto-authenticate in local mode:', error);
    }
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Subscribe to auth state changes
  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.authState));
  }

  private setAuthState(updates: Partial<AuthState>): void {
    this.authState = { ...this.authState, ...updates };
    this.notifyListeners();
    this.saveToStorage();
  }

  getAuthState(): AuthState {
    return this.authState;
  }

  // Generate hashed user ID from email
  private async generateUserId(email: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex.substring(0, 8).toUpperCase(); // First 8 chars, uppercase
  }

  // OAuth Login (Google)
  async loginWithGoogle(): Promise<void> {
    // In local mode, user is already authenticated
    if (this.isLocalMode) {
      console.log('🏠 Already authenticated in local mode');
      return;
    }
    
    this.setAuthState({ isLoading: true, error: null });
    
    try {
      if (this.isDemo) {
        // Demo mode: simulate OAuth with a demo user
        const mockUser: User = {
          id: await this.generateUserId('demo@example.com'),
          email: 'demo@example.com',
          name: 'Demo User',
          provider: 'google',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        };

        this.setAuthState({
          user: mockUser,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
      } else {
        // Production mode: redirect to Cognito OAuth
        if (!this.cognitoConfig) {
          throw new Error('Cognito configuration not found');
        }
        
        const redirectUri = encodeURIComponent(window.location.origin + '/auth/callback');
        const cognitoUrl = `${this.cognitoConfig.domain}/oauth2/authorize?` +
          `response_type=code&` +
          `client_id=${this.cognitoConfig.clientId}&` +
          `redirect_uri=${redirectUri}&` +
          `scope=email+openid+profile&` +
          `identity_provider=Google`;
        
        window.location.href = cognitoUrl;
      }
    } catch (error) {
      this.setAuthState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed'
      });
    }
  }



  // Logout
  async logout(): Promise<void> {
    // In local mode, prevent logout (re-authenticate immediately)
    if (this.isLocalMode) {
      console.log('🏠 Cannot logout in local mode - re-authenticating');
      await this.autoAuthenticateLocalMode();
      return;
    }
    
    this.setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
    this.clearStorage();
    if (this.cognitoConfig) {
      const logoutUri = encodeURIComponent(`${window.location.origin}/auth/logout`);
      window.location.href = `${this.cognitoConfig.domain}/logout?client_id=${this.cognitoConfig.clientId}&logout_uri=${logoutUri}`;
    }
  }

  // Get current user ID for data operations
  getCurrentUserId(): string | null {
    return this.authState.user?.id || null;
  }

  isAdmin(): boolean {
    const email = this.authState.user?.email?.toLowerCase();
    return email === 'dan@zone4.ca' || email === 'alex@zone4.ca' || email === 'etienne@zone4.ca';
  }

  async getIdToken(): Promise<string | null> {
    const token = localStorage.getItem('cognito_id_token');
    if (!token) return null;
    if (!this.isJwtExpired(token, 60)) return token;
    return this.refreshTokens();
  }

  private isJwtExpired(token: string, skewSeconds: number): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return !payload.exp || payload.exp * 1000 < Date.now() + skewSeconds * 1000;
    } catch {
      return true;
    }
  }

  private async refreshTokens(): Promise<string | null> {
    const refreshToken = localStorage.getItem('cognito_refresh_token');
    if (!refreshToken || !this.cognitoConfig) {
      return null;
    }
    try {
      const tokenResponse = await fetch(`${this.cognitoConfig.domain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: this.cognitoConfig.clientId,
          refresh_token: refreshToken,
        }),
      });
      if (!tokenResponse.ok) return null;
      const tokens = await tokenResponse.json();
      localStorage.setItem('cognito_access_token', tokens.access_token);
      localStorage.setItem('cognito_id_token', tokens.id_token);
      return tokens.id_token as string;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isLoggedIn(): boolean {
    return this.authState.isAuthenticated && this.authState.user !== null;
  }

  // Check if in demo mode
  isDemoMode(): boolean {
    return this.isDemo;
  }

  // Check if in local development mode
  isLocalDevelopmentMode(): boolean {
    return this.isLocalMode;
  }

  // Handle OAuth callback (production mode)
  async handleOAuthCallback(code: string): Promise<void> {
    if (this.isDemo || !this.cognitoConfig) {
      return;
    }

    this.setAuthState({ isLoading: true, error: null });

    try {
      // Exchange code for tokens
      const tokenResponse = await fetch(`${this.cognitoConfig.domain}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: this.cognitoConfig.clientId,
          code: code,
          redirect_uri: window.location.origin + '/auth/callback'
        })
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to exchange code for tokens');
      }

      const tokens = await tokenResponse.json();
      
      // Decode ID token to get user info
      const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));
      
      const user: User = {
        id: await this.generateUserId(idTokenPayload.email),
        email: idTokenPayload.email,
        name: idTokenPayload.name || idTokenPayload.given_name || idTokenPayload.email.split('@')[0],
        provider: idTokenPayload.identities ? idTokenPayload.identities[0].providerName.toLowerCase() : 'cognito',
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      };

      // Store tokens securely (consider using httpOnly cookies in production)
      localStorage.setItem('cognito_access_token', tokens.access_token);
      localStorage.setItem('cognito_id_token', tokens.id_token);
      if (tokens.refresh_token) {
        localStorage.setItem('cognito_refresh_token', tokens.refresh_token);
      }

      this.setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

    } catch (error) {
      this.setAuthState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'OAuth callback failed'
      });
    }
  }

  // Storage operations
  private saveToStorage(): void {
    try {
      if (this.authState.user) {
        localStorage.setItem('race-series-auth', JSON.stringify({
          user: this.authState.user,
          isAuthenticated: this.authState.isAuthenticated
        }));
      }
    } catch (error) {
      console.warn('Failed to save auth state to localStorage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('race-series-auth');
      if (stored) {
        const { user, isAuthenticated } = JSON.parse(stored);
        if (user && isAuthenticated) {
          if (!this.isLocalMode && !localStorage.getItem('cognito_id_token')) {
            this.clearStorage();
            return;
          }
          this.authState = {
            ...this.authState,
            user,
            isAuthenticated: true
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load auth state from localStorage:', error);
      this.clearStorage();
    }
  }

  private clearStorage(): void {
    try {
      localStorage.removeItem('race-series-auth');
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_refresh_token');
    } catch (error) {
      console.warn('Failed to clear auth storage:', error);
    }
  }
}

export default AuthService; 